import axios from "axios";

export const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("cellar_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Access tokens last 12h; the refresh cookie lasts 30 days. On the first 401 we renew once (single-flight) and replay the request;
// if that fails the session is over and the app signs the user out instead of leaving "Token expired" in every panel.
let refreshing: Promise<string | null> | null = null;
async function renewAccessToken(): Promise<string | null> {
  if (!refreshing) {
    refreshing = axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`, {}, { withCredentials: true })
      .then((r) => { const t = r.data?.access_token as string | undefined; if (t) localStorage.setItem("cellar_token", t); return t || null; })
      .catch(() => null)
      .finally(() => { setTimeout(() => { refreshing = null; }, 0); });
  }
  return refreshing;
}
api.interceptors.response.use(undefined, async (error) => {
  const cfg = error?.config || {};
  const url: string = cfg.url || "";
  const detail = error?.response?.data?.detail;
  const isAuthCall = url.startsWith("/auth/login") || url.startsWith("/auth/refresh") || url.startsWith("/auth/register") || url.startsWith("/auth/invite") || url.startsWith("/auth/reset") || url.startsWith("/auth/forgot");
  if (error?.response?.status === 401 && !cfg.__retried && !isAuthCall && localStorage.getItem("cellar_token")) {
    cfg.__retried = true;
    const t = await renewAccessToken();
    if (t) { cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${t}` }; return api.request(cfg); }
    if (detail === "Token expired" || detail === "Invalid token" || detail === "Signed out because the password was changed") {
      localStorage.removeItem("cellar_token");
      window.dispatchEvent(new CustomEvent("cellar:session-expired"));
    }
  }
  return Promise.reject(error);
});

export function errorMessage(e: any): string {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return d?.msg || String(d);
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  flagged: boolean;
  menu_groups?: string[];
  marketing?: { asked: boolean; status: string };
  view_only_groups?: string[];
  hidden_sections?: string[];
  is_owner?: boolean;
  role_title?: string;
  picture?: string;
}

export interface Variant {
  id: string;
  title: string;
  sku?: string;
  price_pence: number;
  available: boolean;
}

export interface Product {
  id: string;
  handle: string;
  title: string;
  vendor?: string;
  product_type?: string;
  tags: string[];
  description_html: string;
  images: string[];
  image: string | null;
  variants: Variant[];
  price_min_pence: number;
  available: boolean;
}

export interface Collection {
  id: string;
  handle: string;
  title: string;
  image?: string | null;
  products_count: number | null;
  featured: boolean;
}

export interface Wallet {
  balance_pence: number;
  club_balance_pence: number;
  total_pence: number;
  pending_pence: number;
  currency: string;
  flagged: boolean;
  auto_topup_enabled: boolean;
  auto_topup_threshold_pence: number;
  auto_topup_amount_pence: number;
  mandate_status: string;
}

export interface LedgerEntry {
  id: string;
  txn_id: string;
  kind: string;
  description: string;
  signed_amount_pence: number;
  direction: "credit" | "debit";
  ref: Record<string, string>;
  created_at: string;
}

export interface Payment {
  id: string;
  gc_payment_id: string | null;
  amount_pence: number;
  description: string;
  kind: string;
  status: string;
  charge_date?: string;
  ledger_txn_id?: string | null;
  mock: boolean;
  created_at: string;
}

export interface Subscription {
  id: string;
  name: string;
  amount_pence: number;
  interval_unit: string;
  interval: number;
  day_of_month: number;
  status: string;
  next_charge_date?: string | null;
  skip_next: boolean;
  club?: boolean;
}

export interface Mandate {
  id?: string;
  status: string;
  gc_mandate_id?: string | null;
  billing_request_id?: string;
  mock: boolean;
}

export interface OrderItem {
  product_handle: string;
  variant_id: string;
  title: string;
  variant_title: string;
  image: string | null;
  unit_price_pence: number;
  quantity: number;
  line_total_pence: number;
}

export interface Order {
  id: string;
  items: OrderItem[];
  total_pence: number;
  status: string;
  shopify_order_id?: string | null;
  shopify_order_name?: string | null;
  shopify_mock?: boolean;
  tracking?: { company?: string; number?: string; url?: string | null } | null;
  delivery_method?: "delivery" | "collection";
  payment_method?: string;
  shipping_address?: { name: string; address1: string; city: string; postcode: string } | null;
  status_history: { status: string; at: string }[];
  created_at: string;
}

export interface AppConfig {
  currency: string;
  gocardless_mock: boolean;
  gocardless_environment: string;
  gocardless_webhook_configured: boolean;
  emails_enabled: boolean;
  shopify_admin_mock: boolean;
  shopify_storefront_mode: string;
  store_domain: string;
  stripe_mode: string;
  card_payments: boolean;
  shopify_sso?: boolean;
  collection_point?: { name: string; address1: string; city: string; postcode: string; phone: string; label: string };
}

/** Ask the backend to refresh a GoCardless-backed feed: it answers instantly from the cache and pulls in the background; we poll until the pull lands. */
export async function refreshInBackground<T extends { refreshing?: boolean; pulled_at?: string | null }>(url: string, onData: (d: T) => void, maxMs = 180000): Promise<T> {
  const { data: first } = await api.get<T>(url, { params: { refresh: true } });
  onData(first);
  if (!first.refreshing) return first;
  const started = Date.now();
  while (Date.now() - started < maxMs) {
    await new Promise((r) => setTimeout(r, 3000));
    const { data } = await api.get<T>(url);
    if (!data.refreshing && data.pulled_at !== first.pulled_at) { onData(data); return data; }
  }
  throw new Error("GoCardless is taking longer than usual — the table will update on its own when the pull finishes");
}
